import { ConfigProvider, Descriptions, Drawer, Image, Modal, Spin, Tag } from "antd";
import PropTypes from "prop-types";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { HiDotsHorizontal } from "react-icons/hi";
import { useTranslation } from "react-i18next"; // Added import for translations
import PhotoIcon from "../../../../assets/icons/photo.svg";
import { DataTable, useTableFilters, RowActionsMenu } from "../../../../components/data-table";
import apiClient from "../../../../helpers/apiClient";
import { useCategoriesContext } from "../../../../contexts/CategoriesContext";
import { useToast } from "../../../../contexts/ToastContext";
import useCarPricingMode from "../../hooks/useCarPricingMode";
import deleteFile from "../../../../utilities/deleteFile";
import ExchangeCategoryModal from "./ExchangeCategoryModal";
import normalizeRate from "./categoryRateNormalize";
import CategoryPageHeader from "./CategoryPageHeader";
import CategoryDetailsContent from "../CategoryDetailsContent";

const CATEGORY_CARS_TABLE_SCROLL_X = 750;

const CATEGORY_DETAILS_THEME = {
    token: {
        colorBgContainer: "#ffffff",
        colorBgElevated: "#ffffff",
        borderColor: "#E5E7EB",
    },
    components: {
        Descriptions: {
            labelBg: "#FAFAFA",
        },
    },
};

const descriptionsContentStyle = { backgroundColor: "#ffffff" };
const categoryDetailDescriptionsClassName =
    "category-detail-descriptions [&_.ant-descriptions-view]:overflow-hidden [&_.ant-descriptions-view]:rounded-lg [&_.ant-descriptions-view]:bg-white";

const formatRateDisplay = (tier, t) => {
    const normalized = normalizeRate(tier);
    if (normalized.disabled) {
        return t("disabled");
    }
    return `$${normalized.rate}`;
};

const CategoryDetails = ({
    embedded = false,
    mobileLayout = false,
    open = false,
    setOpen,
    setShowRates,
    categoryId,
    onCancel,
    onEdit,
    onDeleted,
}) => {
    const { t } = useTranslation("cars"); // Added translation hook
    const history = useHistory();
    const toast = useToast();
    const { updateCategory, removeCategory } = useCategoriesContext();
    const isCarPricingMode = useCarPricingMode();
    const [initialLoading, setInitialLoading] = useState(false);
    const [categoryData, setCategoryData] = useState(null);
    const [cars, setCars] = useState([]);
    const [carsLoading, setCarsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchParams, setSearchParams] = useState({});
    const [isSearching, setIsSearching] = useState(false);
    const currentPaginationRef = useRef({ current: 1, pageSize: 10 });
    const [exchangeModalOpen, setExchangeModalOpen] = useState(false);
    const [selectedCarForExchange, setSelectedCarForExchange] = useState(null);
    const [mobileCarSearch, setMobileCarSearch] = useState("");
    const [categoryActionLoading, setCategoryActionLoading] = useState(false);

    const reloadCategoryData = useCallback(async () => {
        if (!categoryId) {
            return;
        }
        try {
            const response = await apiClient.get(`/api/car-category-v2/list?categoryId=${categoryId}`);
            if (response?.data?.success && response.data.data.length > 0) {
                setCategoryData(response.data.data[0]);
            }
        } catch (_error) {
            // keep existing categoryData on refresh failure
        }
    }, [categoryId]);

    // Fetch category data when component opens
    useEffect(() => {
        let cancelled = false;

        const fetchCategoryData = async () => {
            if ((!open && !embedded) || !categoryId) {
                return;
            }
            setCategoryData(null);
            setInitialLoading(true);
            try {
                const response = await apiClient.get(`/api/car-category-v2/list?categoryId=${categoryId}`);
                if (cancelled) return;
                if (response?.data?.success && response.data.data.length > 0) {
                    setCategoryData(response.data.data[0]);
                }
            } catch (error) {
                if (!cancelled) {
                    toast.error({
                        message: t("rates.failedToFetchCategory"),
                        description: error.response?.data?.message || error.message || t("rates.unknownError"),
                    });
                }
            } finally {
                if (!cancelled) {
                    setInitialLoading(false);
                }
            }
        };

        fetchCategoryData();
        return () => {
            cancelled = true;
        };
    }, [open, embedded, categoryId, t, toast]);

    const categoryObjectId = categoryData?._id ?? categoryData?.id ?? null;

    const fetchCategoryCars = useCallback(
        async (page = 1, pageSizeParam = 10, searchParameters = {}) => {
            if ((!open && !embedded) || !categoryObjectId) {
                return;
            }

            setCarsLoading(true);
            try {
                const queryParams = new URLSearchParams({
                    category: String(categoryObjectId),
                    limit: pageSizeParam.toString(),
                    page: page.toString(),
                    ...searchParameters,
                });

                const response = await apiClient.get(`/api/car/list?${queryParams}`);

                if (response?.data?.success) {
                    const { meta, result } = response.data.data;
                    setTotalItems(meta?.total || 0);
                    setCars(
                        (result || []).map((car, index) => ({
                            ...car,
                            key: car.id || index,
                        })),
                    );
                    currentPaginationRef.current = {
                        current: page,
                        pageSize: pageSizeParam,
                    };
                }
            } catch (_error) {
                setCars([]);
                setTotalItems(0);
            } finally {
                setCarsLoading(false);
            }
        },
        [open, embedded, categoryObjectId],
    );

    const table = useTableFilters({
        value: searchParams,
        onChange: (next) => {
            setSearchParams(next);
            setIsSearching(true);
        },
    });

    const handleExchangeCategory = (car) => {
        setSelectedCarForExchange(car);
        setExchangeModalOpen(true);
    };

    const handleExchangeModalClose = () => {
        setExchangeModalOpen(false);
        setSelectedCarForExchange(null);
    };

    const handleExchangeSuccess = () => {
        const exchangedCarId = selectedCarForExchange?.id;
        if (exchangedCarId) {
            setCars((prev) => prev.filter((car) => car.id !== exchangedCarId));
            setTotalItems((prev) => Math.max(0, prev - 1));
        }
        const { current, pageSize: pageSizeRef } = currentPaginationRef.current;
        fetchCategoryCars(current, pageSizeRef, searchParams);
    };

    const handleMobileCarAction = (key, car) => {
        if (key === "carDetails") {
            history.push(`/cardetails/${car?.VIN || ""}`);
            return;
        }
        if (key === "exchangeCategory") {
            handleExchangeCategory(car);
        }
    };

    const carColumns = [
        {
            title: t("carImage"),
            dataIndex: ["images", "image1"],
            key: "carImage",
            width: 100,
            align: "center",
            onCell: () => ({
                style: { paddingTop: 0, paddingBottom: 0 },
            }),
            render: (image) => (
                <div className="overflow-hidden rounded-md flex items-center justify-center">
                    {image ? (
                        <Image
                            src={image}
                            alt=""
                            width={64}
                            height={48}
                            className="rounded-md object-cover"
                            style={{ objectFit: "cover" }}
                        />
                    ) : (
                        <img src={PhotoIcon} alt="" className="h-6 w-6 opacity-40" />
                    )}
                </div>
            ),
        },
        {
            title: t("carName"),
            dataIndex: "carName",
            key: "carName",
            width: 230,
            ellipsis: true,
            ...table.getColumnSearchProps("carName"),
            render: (carName) => carName || "N/A",
        },
        {
            title: t("vinNumberShort"),
            dataIndex: "VIN",
            key: "VIN",
            width: 200,
            ...table.getColumnSearchProps("VIN"),
            render: (vin) => vin || "N/A",
        },
        {
            title: t("plateNumber"),
            dataIndex: "carNumber",
            key: "carNumber",
            width: 120,
            ...table.getColumnSearchProps("carNumber"),
            render: (carNumber, car) => <a href={`/cardetails/${car?.VIN || ""}`}>{carNumber || "N/A"}</a>,
        },
        {
            title: t("action"),
            key: "action",
            width: 100,
            align: "center",
            fixed: "right",
            render: (_, car) => {
                const actionItems = [
                    { label: t("carDetails"), key: "carDetails" },
                    { label: t("rates.exchangeCategory"), key: "exchangeCategory" },
                ];

                const onClickAction = ({ key }) => {
                    if (key === "carDetails") {
                        history.push(`/cardetails/${car?.VIN || ""}`);
                        return;
                    }
                    if (key === "exchangeCategory") {
                        handleExchangeCategory(car);
                    }
                };

                return (
                    <RowActionsMenu items={actionItems} onClick={onClickAction} label={t("action")} />
                );
            },
        },
    ];

    useEffect(() => {
        const { current: currentPageRef, pageSize: pageSizeRef } = currentPaginationRef.current;
        const targetPage = isSearching ? 1 : currentPageRef;
        fetchCategoryCars(targetPage, pageSizeRef, searchParams);

        if (isSearching) {
            setIsSearching(false);
            setCurrentPage(1);
        }
    }, [searchParams, isSearching, fetchCategoryCars]);

    useEffect(() => {
        fetchCategoryCars(currentPage, pageSize, searchParams);
    }, [currentPage, pageSize, fetchCategoryCars, searchParams]);

    useEffect(() => {
        if (open || embedded) {
            setSearchParams({});
            setMobileCarSearch("");
            setCurrentPage(1);
            fetchCategoryCars(1, pageSize, {});
        }
    }, [open, embedded, categoryId, fetchCategoryCars, pageSize]);

    useEffect(() => {
        if (!mobileLayout) {
            return undefined;
        }
        const trimmed = mobileCarSearch.trim();
        const timer = setTimeout(() => {
            setSearchParams(trimmed ? { carName: trimmed } : {});
            setIsSearching(true);
        }, 300);
        return () => clearTimeout(timer);
    }, [mobileCarSearch, mobileLayout]);

    const onClose = () => {
        setCategoryData(null);
        setCars([]);
        setSearchParams({});
        setCurrentPage(1);
        setTotalItems(0);

        if (embedded && onCancel) {
            onCancel();
            return;
        }
        setOpen?.(false);
        setShowRates?.(true);
    };

    const categoryActionItems = useMemo(() => {
        if (!categoryData) {
            return [];
        }
        return [
            { label: t("edit"), key: "edit" },
            {
                label:
                    categoryData.status === "active" ? t("rates.setAsDeactive") : t("rates.setAsActive"),
                key: "toggleStatus",
            },
            { label: t("delete"), key: "delete", danger: true },
        ];
    }, [categoryData, t]);

    const handleCategoryStatusChange = async () => {
        if (!categoryData?.categoryId) {
            return;
        }
        setCategoryActionLoading(true);
        try {
            const newStatus = categoryData.status === "active" ? "inactive" : "active";
            const response = await apiClient.patch(`/api/car-category-v2/${categoryData.categoryId}`, {
                status: newStatus,
            });

            if (response?.data?.success) {
                setCategoryData((prev) => (prev ? { ...prev, status: newStatus } : prev));
                updateCategory(categoryData.categoryId, { status: newStatus });
                toast.success({
                    message: t("rates.statusUpdated"),
                    description: t(`rates.categoryIsNow${newStatus === "active" ? "Active" : "Inactive"}`),
                });
                await reloadCategoryData();
            }
        } catch (error) {
            toast.error({
                message: t("rates.failedToUpdateStatus"),
                description: error.response?.data?.message || error.message,
            });
        } finally {
            setCategoryActionLoading(false);
        }
    };

    const handleCategoryDelete = () => {
        if (!categoryData?.categoryId) {
            return;
        }

        Modal.confirm({
            title: t("rates.areYouSure"),
            content: t("rates.cantRevert"),
            okText: t("yes"),
            cancelText: t("no"),
            centered: true,
            okButtonProps: { danger: true },
            async onOk() {
                setCategoryActionLoading(true);
                try {
                    const response = await apiClient.delete(`/api/car-category-v2/${categoryData.categoryId}`);

                    if (response?.data?.success) {
                        toast.success({
                            message: t("rates.deleted"),
                            description: t("rates.categoryDeleted"),
                        });

                        if (categoryData.image) {
                            await deleteFile(categoryData.image);
                        }

                        removeCategory(categoryData.categoryId);
                        if (onDeleted) {
                            onDeleted();
                        } else {
                            onClose();
                        }
                    }
                } catch (error) {
                    toast.error({
                        message: t("rates.error"),
                        description:
                            error.response?.data?.message || error.message || t("rates.failedToDeleteCategory"),
                    });
                } finally {
                    setCategoryActionLoading(false);
                }
            },
        });
    };

    const handleCategoryMenuClick = ({ key }) => {
        if (!categoryData) {
            return;
        }
        if (key === "edit") {
            onEdit?.();
            return;
        }
        if (key === "toggleStatus") {
            handleCategoryStatusChange();
            return;
        }
        if (key === "delete") {
            handleCategoryDelete();
        }
    };

    const categoryActionsMenu =
        categoryData && !initialLoading ? (
            <RowActionsMenu
                items={categoryActionItems}
                onClick={handleCategoryMenuClick}
                label={t("action")}
                disabled={categoryActionLoading}
                loading={categoryActionLoading}
                icon={<HiDotsHorizontal className="text-xl text-[#667085]" />}
                className="!h-9 !w-9 !rounded-lg"
            />
        ) : null;

    const headerActions = categoryActionsMenu;

    const isCategoryActive = categoryData?.status === "active";

    const basicInfoItems = [
        {
            key: "categoryName",
            label: t("rates.categoryName"),
            children: categoryData?.categoryName || "N/A",
            span: isCarPricingMode ? 1 : 2,
        },
        {
            key: "status",
            label: t("status"),
            children: (
                <Tag color={isCategoryActive ? "green" : "red"}>
                    {isCategoryActive ? t("rates.active") : t("rates.deactive")}
                </Tag>
            ),
        },
        ...(!isCarPricingMode
            ? [
                  {
                      key: "seats",
                      label: t("seats"),
                      children: categoryData?.seats ?? "N/A",
                  },
                  {
                      key: "bags",
                      label: t("rates.bags"),
                      children: categoryData?.bags ?? "N/A",
                  },
              ]
            : []),
    ];

    const rateItems = !isCarPricingMode
        ? [
              {
                  key: "hourly",
                  label: t("rates.perHour"),
                  children: formatRateDisplay(categoryData?.hourlyRate, t),
              },
              {
                  key: "daily",
                  label: t("rates.perDay"),
                  children: formatRateDisplay(categoryData?.dailyRate, t),
              },
              {
                  key: "weekly",
                  label: t("rates.perWeek"),
                  children: formatRateDisplay(categoryData?.weeklyRate, t),
              },
              {
                  key: "monthly",
                  label: t("rates.perMonth"),
                  children: formatRateDisplay(categoryData?.monthlyRate, t),
              },
          ]
        : [];

    const categoryImageContent = categoryData?.image ? (
        <Image
            src={categoryData.image}
            alt={t("rates.categoryImage")}
            width={270}
            height={270}
            className="rounded-xl object-cover"
            style={{ objectFit: "cover" }}
        />
    ) : (
        <div className="flex h-[270px] w-[270px] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-white text-[#acacb9]">
            <img src={PhotoIcon} alt="" className="h-12 w-12 opacity-40" />
            <span className="text-sm">{t("rates.categoryImage")}</span>
        </div>
    );

    const pageContent = (
        <ConfigProvider theme={CATEGORY_DETAILS_THEME}>
            <div className="relative flex flex-col pb-4">
                {categoryActionLoading ? (
                    <div
                        className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/60"
                        aria-busy="true"
                        aria-live="polite">
                        <Spin size="large" />
                    </div>
                ) : null}
                {initialLoading ? (
                    <div className="flex min-h-[240px] items-center justify-center">
                        <Spin size="large" />
                    </div>
                ) : (
                    <div className="flex w-full flex-col-reverse items-center gap-4 pr-1 2xl:flex-row 2xl:items-center">
                        <div className="w-full min-w-0 2xl:flex-1">
                            <Descriptions
                                bordered
                                title={t("rates.basicInfo")}
                                column={isCarPricingMode ? 1 : 2}
                                size="small"
                                className={categoryDetailDescriptionsClassName}
                                contentStyle={descriptionsContentStyle}
                                items={basicInfoItems}
                            />
                            {!isCarPricingMode && (
                                <Descriptions
                                    bordered
                                    title={t("rates.setPrice")}
                                    column={2}
                                    size="small"
                                    className={`mt-4 ${categoryDetailDescriptionsClassName}`}
                                    contentStyle={descriptionsContentStyle}
                                    items={rateItems}
                                />
                            )}
                        </div>
                        <div className="flex w-full border rounded-md flex-shrink-0 flex-col items-center justify-center 2xl:w-auto">
                            {categoryImageContent}
                        </div>
                    </div>
                )}

                {!initialLoading && (
                    <div className="mt-6 flex flex-col px-1">
                        <p className="mb-3 text-sm text-[#9b9ba5]">{t("rates.categoryCarsSubheading")}</p>
                        <DataTable
                            title={t("carList")}
                            count={totalItems}
                            filters={table}
                            search={{
                                fields: [
                                    { key: "carName", label: t("carName") },
                                    { key: "VIN", label: t("vinNumberShort") },
                                    { key: "carNumber", label: t("plateNumber") },
                                ],
                            }}
                            columns={carColumns}
                            dataSource={cars}
                            loading={carsLoading}
                            tableLayout="fixed"
                            scroll={{ x: CATEGORY_CARS_TABLE_SCROLL_X }}
                            pagination={{
                                total: totalItems,
                                current: currentPage,
                                pageSize,
                                onChange: setCurrentPage,
                                onPageSizeChange: setPageSize,
                            }}
                        />
                    </div>
                )}
            </div>

            <ExchangeCategoryModal
                open={exchangeModalOpen}
                onClose={handleExchangeModalClose}
                car={selectedCarForExchange}
                currentCategoryId={categoryId}
                onSuccess={handleExchangeSuccess}
            />
        </ConfigProvider>
    );

    if (embedded && mobileLayout) {
        return (
            <div className="flex min-w-0 flex-col">
                <CategoryDetailsContent
                    categoryData={categoryData}
                    initialLoading={initialLoading}
                    isCarPricingMode={isCarPricingMode}
                    formatRateDisplay={formatRateDisplay}
                    cars={cars}
                    carsLoading={carsLoading}
                    totalItems={totalItems}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onBack={onClose}
                    categoryActionItems={categoryActionItems}
                    onCategoryActionClick={handleCategoryMenuClick}
                    categoryActionLoading={categoryActionLoading}
                    onCarActionClick={handleMobileCarAction}
                    carSearchValue={mobileCarSearch}
                    onCarSearchChange={setMobileCarSearch}
                />
                <ExchangeCategoryModal
                    open={exchangeModalOpen}
                    onClose={handleExchangeModalClose}
                    car={selectedCarForExchange}
                    currentCategoryId={categoryId}
                    onSuccess={handleExchangeSuccess}
                />
            </div>
        );
    }

    if (embedded) {
        return (
            <div className="flex h-full min-h-0 flex-col overflow-hidden">
                <CategoryPageHeader title={t("rates.categoryDetails")} onBack={onClose} extra={headerActions} />
                <div className="min-h-0 flex-1 overflow-y-auto">{pageContent}</div>
            </div>
        );
    }

    return (
        <Drawer
            title={<span className="text-[1.2rem] fontbold">{t("rates.categoryDetails")}</span>}
            width={"100%"}
            height={"100vh"}
            closeIcon={false}
            open={open}
            afterOpenChange={(visible) => {
                if (visible) {
                    setTimeout(() => {
                        const drawerBody = document.querySelector(".ant-drawer-body");
                        if (drawerBody) {
                            drawerBody.scrollTop = 0;
                        }
                    }, 100);
                }
            }}
            styles={{
                content: {
                    backgroundColor: "#F2F2FA",
                    padding: 5,
                },
                body: {
                    padding: 0,
                    paddingTop: 10,
                    height: "100%",
                    overflow: "auto",
                },
                header: {
                    padding: 0,
                    paddingTop: 5,
                    paddingBottom: 10,
                },
            }}
            extra={headerActions}
            getContainer={false}>
            {pageContent}
        </Drawer>
    );
};

CategoryDetails.propTypes = {
    embedded: PropTypes.bool,
    mobileLayout: PropTypes.bool,
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    setShowRates: PropTypes.func,
    categoryId: PropTypes.string,
    onCancel: PropTypes.func,
    onEdit: PropTypes.func,
    onDeleted: PropTypes.func,
};

export default CategoryDetails;
